import React from 'react';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import translate from '@/lib/intl/translate';
import MatchingHeaderCategory from '../../constants/MatchingHeaderCategory';
import MatchingTableHeaders from '../../constants/MatchingTableHeaders';
import useMatchedHeaders from '../../hooks/useMatchedHeaders';
import type {
  APIHeader,
  MatchType,
} from '../../lib/matchingTableHeadersConstants';
import type APIHeaderMatch from '../../types/APIHeaderMatch';
import type CSVHeaderColumn from '../../types/CSVHeaderColumn';
import type MatchingTableHeader from '../../types/MatchingTableHeader';

type MatchHeadersContextState = {
  matchedHeaders: APIHeaderMatch[];
  updateHeaders(
    apiHeader: APIHeader,
    matchedHeader: CSVHeaderColumn,
    matchType: keyof typeof MatchType,
  ): void;
  headerOptions: MatchingTableHeader[];
};

export const MatchHeadersContext = React.createContext(
  {} as MatchHeadersContextState,
);
type MatchHeadersProviderProps = {
  children: React.ReactNode;
};

export const useMatchHeadersContext = () =>
  React.useContext(MatchHeadersContext);

export default function MatchHeadersProvider({
  children,
}: MatchHeadersProviderProps) {
  const { matchedHeaders, updateHeaders } = useMatchedHeaders();

  const { data: customFields } = useCustomFields();

  /**
   * the full list of available headers should include custom fields, and the whole
   * list should be sorted alphanumerically.
   */
  const headerOptions = React.useMemo(() => {
    if (!customFields) {
      return MatchingTableHeaders;
    }

    return [
      ...MatchingTableHeaders,
      ...customFields.map(
        (field): MatchingTableHeader => ({
          category: MatchingHeaderCategory.CONSTITUENT,
          fieldName: field.customFieldId,
          label: {
            text: field.fieldName,
            tooltip: translate('DATA_UPLOADS_MATCH_HEADERS_CUSTOM_FIELD'),
          },
        }),
      ),
    ].sort((a, b) => {
      return a.category.localeCompare(b.category);
    });
  }, [MatchingTableHeaders, customFields]);

  const value = React.useMemo(
    () => ({
      matchedHeaders,
      updateHeaders,
      headerOptions,
    }),
    [matchedHeaders, updateHeaders, headerOptions],
  );

  return (
    <MatchHeadersContext.Provider value={value}>
      {children}
    </MatchHeadersContext.Provider>
  );
}
